import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Grid, TextField, MenuItem } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import Carouselcomponent from "../../corousel/Carousel-component";
import { FroalaRichText } from "../../util/FroalaRichtext";
import { updateInternationalFormData } from "../../../redux/slices/summaryFormSlice";
import { propertyImages, resetPropertyImagesAction } from "../../../redux/slices/propertyImagesSlice";
import { updateDapCommentData, DapComment } from "../../../redux/slices/updateDapCommentSlice";
import { updateSummary } from "../../../redux/slices/updateDataSlice";
import { GenericToast } from "../../util/ToastMessage";
import { CommentKey, CommentSectionName } from "../../../constant";
import { saveDapCommentData } from "../../../redux/slices/saveCommentSlice";
import { dateTimeToDateFormat, formatClosedPeriod } from "../../util/valueFormatter";

import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/third_party/embedly.min.js";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/third_party/embedly.min.css";
import { getAbfStatusLogs } from "redux/slices/abfStatusSlice";
import { getSustainabilityData } from "redux/slices/sustainabilitySlice";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "left",
  color: theme.palette.text.secondary,
}));

export const SummaryInternationalEditForm = () => {
  const dispatch = useAppDispatch();

  const dapData: any = useAppSelector((state) => state.summary.internationalData);
  const comment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === CommentKey.SUMMARY_KEY.toLowerCase()
  );
  const errorStatus = useAppSelector((state) => ({
    loading: state.summary.loading,
    error: state.summary.error
  }));
  const assetPlanId = useAppSelector((state) => state.getAssetPlanId.assetPlanId);
  const docOnChangeStatus = useAppSelector((state) => state.onchangeFlagForDocComponent.isChanged);
  const portfolioInvestments = useAppSelector((state) => state.abfStatus.data);
  useEffect(() => {
    if (dapData?.investmentId && dapData?.budgetYear && dapData.investmentId.toLowerCase().includes("portfolio")) {
      dispatch(
        getAbfStatusLogs({
          investmentId: dapData.investmentId,
          budgetYear: Number(dapData.budgetYear),
        }),
      );
    }
  }, [dapData?.investmentId, dapData?.budgetYear, dispatch]);

  useEffect(() => {
    if (!dapData?.investmentId) return;

    dispatch(resetPropertyImagesAction());
    
    const investmentIds = dapData.investmentId?.toLowerCase().includes("portfolio")
      ? portfolioInvestments?.map(investment => investment.investmentId).filter(Boolean) || []
      : [dapData.investmentId];
    
    dispatch(propertyImages({ investmentIds }));
  }, [dapData?.investmentId, portfolioInvestments, dispatch]);

  useEffect(() => {
    if (assetPlanId && dapData?.investmentId) {
      const request = {
        assetPlanId: assetPlanId,
        investmentId: dapData?.investmentId,
      };
      dispatch(getSustainabilityData(request));
    }
  }, [assetPlanId, dapData?.investmentId]);

 const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    if (dapData) {
      dispatch(updateInternationalFormData({ ...dapData, [name]: value }));
      dispatch(updateSummary({
        bookCostField: dapData?.bookCostField,
        totalCapEx: dapData?.totalCapEx,
        specialPurposeVehicle: dapData?.specialPurposeVehicle,
        jvPartner: dapData?.jvPartner,
        propertyManagingAgent: dapData?.propertyManagingAgent,
        operator: dapData?.operator,
        keyPMAOperatorContact: dapData?.keyPMAOperatorContact,
    
        investmentId: dapData?.investmentId,
        assetPlanId: assetPlanId,
        [name]: value,
      }));
    }
  };

  const handleCommentChange = (model: any) => {
    const updatedcomment: DapComment = {
      id: comment ? comment.id : 0,
      commentKey: comment ? comment.commentKey : CommentKey.SUMMARY_KEY,
      commentText: model || "",
      sectionName: CommentSectionName.SUMMARY,
      assetPlanId: assetPlanId,
    };
    dispatch(updateDapCommentData(updatedcomment));
    dispatch(saveDapCommentData(updatedcomment));
  };

  return (
    <div className="container ">
      <Box sx={{ width: "100%" }}>
        <div className="data-content">
          <Box sx={{ flexGrow: 1 }} className="summary-form">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {errorStatus.loading ? (
                  <Skeleton variant="rounded" width="100%" height={350} animation="wave" />
                ) : (
                  <Item>
                    <table className="summary-table">
                      <tr>
                        <th><b>ID:</b></th>
                        <td>{dapData?.investmentId}</td>
                      </tr>
                      <tr>
                        <th><b>Fund Name:</b></th>
                        <td>{dapData?.fundName}</td>
                      </tr>
                      <tr>
                        <th><b>City:</b></th>
                        <td>{dapData?.city}</td>
                      </tr>
                      <tr>
                        <th><b>Country:</b></th>
                        <td>{dapData?.country}</td>
                      </tr>
                      <tr>
                        <th><b>Date acquired:</b></th>
                        <td>{dapData?.dateAcquired ? dateTimeToDateFormat(dapData?.dateAcquired) : ""}</td>
                      </tr>
                      <tr>
                        <th><b>Asset manager:</b></th>
                        <td>{dapData?.assetManager?.firstName} {dapData?.assetManager?.lastName}</td>
                      </tr>
                      <tr>
                        <th><b>Fund manager:</b></th>
                        <td>{dapData?.fundManager?.firstName} {dapData?.fundManager?.lastName}</td>
                      </tr>
                      <tr>
                        <th><b>Sector (Property Type):</b></th>
                        <td>{dapData?.sector}</td>
                      </tr>
                      <tr>
                        <th><b>Sub-sector (Property Sub-Type):</b></th>
                        <td>{dapData?.subSector}</td>
                      </tr>
                      <tr>
                        <th><b>Stranding Year, Carbon:</b></th>
                        <td>{dapData?.strandingYearCarbon}</td>
                      </tr>
                      <tr>
                        <th>&nbsp;</th>
                        <td>&nbsp;</td>
                      </tr>
                    </table>
                  </Item>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {errorStatus.loading ? (
                  <Skeleton variant="rounded" width="100%" height={350} animation="wave" />
                ) : (
                  <Item>
                    <table className="summary-table">
                      <tr>
                        <th><b>Ownership type:</b></th>
                        <td>{dapData?.ownershipType}</td>
                      </tr>
                      <tr>
                        <th><b>Fund ownership (%):</b></th>
                        <td>{dapData?.ownerShip ? dapData?.ownerShip.toFixed(2) : ""}</td>
                      </tr>
                      <tr>
                        <th><b>Currency:</b></th>
                        <td>{dapData?.currency}</td>
                      </tr>
                      <tr>
                        <th><b>Book Cost:</b></th>
                        <td>{dapData?.bookCostField}</td>
                      </tr>
                      <tr>
                        <th><b>Book Cost Value:</b></th>
                        <td>{formatClosedPeriod(dapData?.lastClosedPeriod)}</td>
                      </tr>
                      <tr>
                        <th><b>Debt:</b></th>
                         <td>{dapData?.debt === "1" ? "Yes" : "No"}</td>

                      </tr>
                      <tr>
                        <th><b>Total CAPEX :</b></th>
                        <td>{dapData?.totalCapEx}</td>
                      </tr>
                      <tr>
                        <th><b>Special purpose vehicle (Name of PropCo/Entity ownership):</b></th>
                        <td>{dapData?.specialPurposeVehicle}</td>
                      </tr>
                      <tr>
                        <th><b>JV Partner (Nuveen Ownership Type):</b></th>
                        <td>{dapData?.jvPartner}</td>
                      </tr>
                      <tr>
                        <th><b>Property managing agent:</b></th>
                        <td>{dapData?.propertyManagingAgent}</td>
                      </tr>
                      <tr>
                        <th><b>Operator:</b></th>
                        <td>
                          <TextField
                            InputProps={{ style: { margin: "0px", padding: "4px 0px", height: "28px", fontFamily: "Roboto, Helvetica, Arial, sans-serif", fontSize: "0.875rem", color: "rgba(0, 0, 0, 0.6)" } }}
                            inputProps={{ style: { padding: "4px 4px" } }}
                            value={dapData?.operator || ""}
                            name="operator"
                            onChange={handleInputChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th><b>Key PMA / operator contact:</b></th>
                        <td>
                          <TextField
                            InputProps={{ style: { margin: "0px", padding: "4px 0px", height: "28px", fontFamily: "Roboto, Helvetica, Arial, sans-serif", fontSize: "0.875rem", color: "rgba(0, 0, 0, 0.6)" } }}
                            inputProps={{ style: { padding: "4px 4px" } }}
                            value={dapData?.keyPMAOperatorContact || ""}
                            name="keyPMAOperatorContact"
                            onChange={handleInputChange}
                          />
                        </td>
                      </tr>
                    </table>
                  </Item>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ flexGrow: 1 }} className="summary-property-section summary-form">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Item>
                  <h3 className="text-left pl-10">Property Images</h3>
                  <div className="clear"></div>
                  <Carouselcomponent />
                </Item>
              </Grid>
              <Grid item xs={12} md={6}>
                <Item>
                  <h3 className="text-left pl-10">Additional Property Description</h3>
                  <div className="clear"></div>
                  <div style={{ marginLeft: "12px" }}>
                    <FroalaRichText model={comment?.commentText || ""} handlesModelChange={handleCommentChange} />
                  </div>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </div>
      </Box>
      {errorStatus.error && <GenericToast message={errorStatus.error} severity="error" />}
    </div>
  );
};
